/**
 * What a passkey signature is about, in words.
 *
 * The passkey signs the hash of the challenge's canonical payload, so the
 * payload is the one honest description of what is being agreed to. This turns
 * it into a sentence and a few labelled rows. It never shows raw JSON, and it
 * never fills a gap with a guess: a missing field reads "Not stated".
 *
 * Pure, so every shape the server can send is unit tested.
 */
import type { StepUpPayload, StepUpPurpose } from "state/members/stepUp";

export type PayloadRow = { label: string; value: string };

export type PayloadSummary = {
  /** One sentence: "You are requesting Admin on GitHub for yourself, for 7 days." */
  sentence: string;
  rows: PayloadRow[];
};

export const NOT_STATED = "Not stated";

const SUBMIT = "ploy.access_request.submit";
const APPROVE = "ploy.access_request.approve";
const OVERRIDE = "ploy.access_request.override";

const text = (value: unknown): string | null =>
  typeof value === "string" && value.trim() ? value.trim() : null;

/**
 * An account as the request it came from names it, for the account row. The
 * payload carries only the identity's id.
 */
export type KnownAccount = {
  id: string;
  username?: string | null;
  displayName?: string | null;
  extId?: string | null;
};

/**
 * The account the access is for, or null for a request on the default
 * account. Named the way the portal names an account elsewhere when `known`
 * is that same identity; otherwise by its id, so the same role on two
 * accounts never reads the same.
 */
export const accountLabel = (
  payload: StepUpPayload,
  known?: KnownAccount | null,
): string | null => {
  const identityId = text(payload.entitlement?.identityId);
  if (!identityId) return null;
  if (known?.id !== identityId) return identityId;
  return (
    text(known.username) ?? text(known.displayName) ?? text(known.extId) ?? identityId
  );
};

/** "Admin on GitHub", or whichever half is known. */
export const accessLabel = (payload: StepUpPayload): string | null => {
  const role = text(payload.entitlement?.role);
  const app = text(payload.entitlement?.app);
  if (role && app && role !== app) return `${role} on ${app}`;
  return role ?? app;
};

const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? "" : "s"}`;

/**
 * A duration in the largest unit that states it exactly: 604800 is "7 days",
 * 5400 is "90 minutes". Rounding would show a person something other than
 * what they are signing.
 */
export const durationLabel = (
  duration: StepUpPayload["duration"],
): string | null => {
  if (!duration) return null;
  if (duration.indefinite) return "No end date";
  const seconds = duration.seconds;
  if (typeof seconds !== "number" || !Number.isFinite(seconds) || seconds <= 0) {
    return null;
  }
  if (seconds % 86_400 === 0) return plural(seconds / 86_400, "day");
  if (seconds % 3_600 === 0) return plural(seconds / 3_600, "hour");
  if (seconds % 60 === 0) return plural(seconds / 60, "minute");
  return plural(seconds, "second");
};

/** "Step 2: Security review". Stages are counted from zero on the server. */
export const approvalStepLabel = (
  step: StepUpPayload["approvalStep"],
): string | null => {
  if (!step || typeof step.index !== "number" || step.index < 0) return null;
  const name = text(step.name);
  const number = `Step ${step.index + 1}`;
  return name ? `${number}: ${name}` : number;
};

const sameParty = (payload: StepUpPayload) =>
  Boolean(
    payload.requester?.memberId &&
      payload.requester.memberId === payload.subject?.memberId,
  );

const durationPhrase = (label: string | null) =>
  !label
    ? ""
    : label === "No end date"
      ? ", with no end date"
      : `, for ${label}`;

/**
 * Which kind of signature this is. The payload's own `type` is what was
 * signed, so it wins; the challenge's purpose covers a payload without one.
 */
export const signatureKind = (
  payload: StepUpPayload,
  purpose?: StepUpPurpose,
): "submit" | "approve" | "unknown" => {
  if (payload.type === SUBMIT) return "submit";
  if (payload.type === APPROVE || payload.type === OVERRIDE) return "approve";
  if (!payload.type && purpose === "request_submission") return "submit";
  if (!payload.type && purpose === "approval") return "approve";
  return "unknown";
};

export const describePayload = (
  payload: StepUpPayload,
  purpose?: StepUpPurpose,
  known?: KnownAccount | null,
): PayloadSummary => {
  const kind = signatureKind(payload, purpose);
  const access = accessLabel(payload);
  const account = accountLabel(payload, known);
  const duration = durationLabel(payload.duration);
  const reason = text(payload.justification);
  const step = approvalStepLabel(payload.approvalStep);
  const requester = text(payload.requester?.email);
  const subject = text(payload.subject?.email);
  const forSelf = sameParty(payload);

  // What is granted, then on which account when it is not the default one.
  const accessRows: PayloadRow[] = [
    { label: "Access", value: access ?? NOT_STATED },
    ...(account ? [{ label: "Account", value: account }] : []),
  ];
  const durationRow: PayloadRow = {
    label: "Duration",
    value: duration ?? NOT_STATED,
  };
  const reasonRow: PayloadRow = { label: "Reason", value: reason ?? "None given" };

  if (kind === "submit") {
    const forWhom = forSelf ? "Yourself" : (subject ?? NOT_STATED);
    return {
      sentence:
        `You are requesting ${access ?? "access"} for ` +
        `${forSelf ? "yourself" : (subject ?? "someone else")}` +
        `${durationPhrase(duration)}.`,
      rows: [...accessRows, { label: "For", value: forWhom }, durationRow, reasonRow],
    };
  }

  if (kind === "approve") {
    const rows: PayloadRow[] = [
      ...accessRows,
      { label: "Requested by", value: requester ?? NOT_STATED },
      {
        label: "For",
        value: forSelf ? "The requester" : (subject ?? NOT_STATED),
      },
      durationRow,
      reasonRow,
    ];
    if (step) rows.push({ label: "Approval step", value: step });
    return {
      sentence:
        `You are approving ${access ?? "access"} for ` +
        `${subject ?? requester ?? "someone"}${durationPhrase(duration)}.`,
      rows,
    };
  }

  return {
    sentence: "You are signing an access request.",
    rows: [...accessRows, durationRow, reasonRow],
  };
};
